import { customAlphabet, nanoid, random, urlAlphabet } from 'nanoid'

import { ALPHABET, ALPHABET_UPPER, BINARY, HEX, NUMBERS, OCTAL } from './charset'
import type { Charset } from './types'

/**
 * Generate a random string
 *
 * @param len {number} default 30
 * @returns {String}
 */
export function randomString(len = 30, charset: Charset = 'url'): string {
  let chars: string

  switch (charset) {
    case 'alphanumeric': {
      chars = NUMBERS + ALPHABET + ALPHABET_UPPER
      break
    }
    case 'numeric': {
      chars = NUMBERS
      break
    }
    case 'alphabetic': {
      chars = ALPHABET + ALPHABET_UPPER
      break
    }
    case 'hex': {
      chars = HEX
      break
    }
    case 'binary': {
      chars = BINARY
      break
    }
    case 'octal': {
      chars = OCTAL
      break
    }
    default: {
      chars = urlAlphabet
      break
    }
  }

  return customAlphabet(chars, len)()
}

/**
 * Random bytes
 *
 * @param len {Number} default 16
 */
export function randomBytes(len = 16): Uint8Array {
  return random(len)
}

/**
 * Generate a random number
 *
 * https://github.com/EFForg/OpenWireless/blob/master/app/js/diceware.js#L59
 *
 * @param max {Number} default max
 * @returns {String}
 */
export function randomInt(min = 0, max = 1000): number {
  let rval = 0
  const range = max - min

  const bitsNeeded = Math.ceil(Math.log2(range))

  if (bitsNeeded > 53) {
    throw new Error('We cannot generate numbers larger than 53 bits.')
  }
  const bytesNeeded = Math.ceil(bitsNeeded / 8)
  const mask = 2 ** bitsNeeded - 1
  // 7776 -> (2^13 = 8192) -1 == 8191 or 0x00001111 11111111

  // Create byte array and fill with N random numbers
  const byteArray = randomBytes(bytesNeeded)

  let p = (bytesNeeded - 1) * 8

  for (let i = 0; i < bytesNeeded; i += 1) {
    rval += byteArray[i] * 2 ** p
    p -= 8
  }

  // Use & to apply the mask and reduce the number of recursive lookups
  rval &= mask

  if (rval >= range) {
    // Integer out of acceptable range
    return randomInt(min, max)
  }

  // Return an integer that falls within the range
  return min + rval
}

export { nanoid as randomId }
